import { useQuery } from "@tanstack/react-query"
import { client } from "@/api"

interface UsageMetrics {
  storage: {
    used_bytes: number
    limit_bytes: number
    used_gb: number
    limit_gb: number
    percentage: number
  }
  games: {
    count: number
    limit: number
    percentage: number
  }
  clips: {
    count: number
    limit: number
    percentage: number
  }
  members?: {
    count: number
    limit: number
    percentage: number
  }
}

interface TeamUsageResponse {
  type: "team"
  id: string
  name: string
  role?: string
  plan: {
    id: string
    name: string
    tier: string
    features: string[]
  }
  usage: UsageMetrics
  limits: {
    storage_gb: number
    max_games: number
    max_team_members?: number
  }
}

export interface UsageAnomaly {
  type: "high_usage" | "rapid_growth" | "unusual_pattern"
  severity: "low" | "medium" | "high"
  message: string
  metric: string
  value: number
  threshold: number
  timestamp: string
}

interface IndividualUsageResponse {
  type: "individual"
  id: string
  name: string
  plan: {
    id: string
    name: string
    tier: string
    features: string[]
  }
  usage: UsageMetrics
  limits: {
    storage_gb: number
    max_games: number
  }
  anomalies?: UsageAnomaly[]
}

export function useTeamLimits(teamId: string) {
  return useQuery<TeamUsageResponse>({
    queryKey: ["limits", "team", teamId],
    queryFn: async () => {
      // Admin-only endpoint (#1985): the coach endpoint `GET /v1/limits/usage` answers only
      // members of the team, so a superuser who isn't a member got a 403 and no dashboard.
      const response = await client.admin.teams[":publicId"].usage.$get({
        param: { publicId: teamId },
      })

      if (!response.ok) {
        throw new Error("Failed to fetch team limits")
      }

      return (await response.json()) as TeamUsageResponse
    },
    enabled: !!teamId,
  })
}

export function useUserLimits(userId: string) {
  return useQuery<IndividualUsageResponse | TeamUsageResponse[]>({
    queryKey: ["limits", "user", userId],
    queryFn: async () => {
      // For user limits, we need to fetch without teamId to get all their data
      // This would need a backend change to support fetching by specific user ID for admin
      // For now, we'll return a placeholder
      throw new Error("User limits endpoint not yet implemented for admin")
    },
    enabled: false, // Disabled until backend support is added
  })
}

export function useIndividualLimits(userId: string) {
  return useQuery<IndividualUsageResponse>({
    queryKey: ["limits", "individual", userId],
    queryFn: async () => {
      // Note: This endpoint doesn't exist in the backend yet
      // The usage data should come from the v1/limits/usage endpoint
      throw new Error("Individual limits endpoint not yet implemented for admin")
    },
    enabled: false, // Disabled until backend support is added
  })
}

